import { useEffect, useState } from 'react';
import { useSetMoneyType, useSetOnboardingStage, useStripeOnboardingStatus } from '../state';
import fetchWithToken, { updateOnboardingStage } from '../services';
import PageLayout from './PageLayout';
import bitcoinIcon from '../assets/icons/bitcoin-simple.svg';
import currenciesIcon from '../assets/icons/3currencies.png';
import { useNavigate } from 'react-router-dom';
import { ONBOARDING_ROUTES, GENERAL_ROUTES } from '../constants/routes';

const SelectMoneyType = () => {
  const setMoneyType = useSetMoneyType();
  const setOnboardingStage = useSetOnboardingStage();
  const stripeOnboardingStatus = useStripeOnboardingStatus();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // If Stripe onboarding is complete and user selected fiat only, navigate to home
    if (stripeOnboardingStatus === 'complete') {
      navigate('/');
    }
  }, [stripeOnboardingStatus, navigate]);

  const updateMoneyType = async (option) => {
    setIsLoading(true);
    try {
      const response = await fetchWithToken('/update-money-type', {
        method: 'PATCH',
        body: JSON.stringify({ moneyType: option }),
      });
      if (!response.ok) {
        throw new Error(`Error updating moneyType on server: ${response.statusText}`);
      }
      setMoneyType(option);

      // Update onboarding stage based on selection
      let nextStage;
      let nextRoute;
      switch (option) {
        case 'fiat':
          // If Stripe onboarding is already complete, skip to complete stage
          if (stripeOnboardingStatus === 'complete') {
            nextStage = 'complete';
            nextRoute = GENERAL_ROUTES.HOME;
          } else {
            nextStage = 'fiat-onboarding';
            nextRoute = ONBOARDING_ROUTES.FIAT_ONBOARDING;
          }
          break;
        case 'crypto':
          nextStage = 'crypto-onboarding';
          nextRoute = ONBOARDING_ROUTES.CRYPTO_ONBOARDING;
          break;
        case 'both':
          nextStage = 'fiat-crypto-onboarding';
          nextRoute = ONBOARDING_ROUTES.FIAT_CRYPTO_ONBOARDING;
          break;
        default:
          throw new Error('Invalid money type selected');
      }

      await updateOnboardingStage(nextStage);
      setOnboardingStage(nextStage);
      navigate(nextRoute);
    } catch (error) {
      console.error('Error updating moneyType on server: ', error);
      navigate(GENERAL_ROUTES.ERROR);
    }
    setIsLoading(false);
  };

  const handleBack = async () => {
    setIsLoading(true);
    try {
      await updateOnboardingStage('enter-link-id');
      setOnboardingStage('enter-link-id');
      navigate(ONBOARDING_ROUTES.ENTER_LINK_ID);
    } catch (error) {
      console.error('Error going back:', error);
      navigate(GENERAL_ROUTES.ERROR);
    }
    setIsLoading(false);
  };

  return (
    <PageLayout>
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh)]">
        <div>
          <div className="w-full max-w-4xl px-4">
            <h2 className="text-white mb-8 text-center text-lg">How would you like your supporters to be able to pay you?</h2>
            <div className="flex flex-row gap-6 justify-center">
              <button
                className="flex flex-col items-center rounded-lg p-6 hover:ring-2 hover:ring-white transition-all w-48"
                onClick={() => updateMoneyType('fiat')}
                disabled={isLoading}
              >
                <div className="h-16 flex items-center mb-4">
                  <img src={currenciesIcon} alt="National Currencies" className="w-16 h-16" />
                </div>
                <span className="text-white">Regular currencies</span>
              </button>
              <button
                className="flex flex-col items-center rounded-lg p-6 hover:ring-2 hover:ring-white transition-all w-48"
                onClick={() => updateMoneyType('crypto')}
                disabled={isLoading}
              >
                <div className="h-16 flex items-center mb-4">
                  <img src={bitcoinIcon} alt="Cryptocurrency" className="w-16 h-16" />
                </div>
                <span className="text-white">Cryptocurrencies</span>
              </button>
              <button
                className="flex flex-col items-center rounded-lg p-6 hover:ring-2 hover:ring-white transition-all w-48"
                onClick={() => updateMoneyType('both')}
                disabled={isLoading}
              >
                <div className="h-16 flex items-center justify-center mb-4">
                  <img src={currenciesIcon} alt="National Currencies" className="w-14 h-14 mr-1" />
                  <img src={bitcoinIcon} alt="Cryptocurrency" className="w-14 h-14 ml-1" />
                </div>
                <span className="text-white">Both</span>
              </button>
            </div>
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={handleBack}
                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-300"
                disabled={isLoading}
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default SelectMoneyType;
